import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../configs/supabase';
import { OrderSummary } from '../../types/OrderSummary';
import { Order } from '../../types/Order';
import { DataFetchError } from '../../utils/ErrorClass';

export const useOrderSummary = () => {
  const [orderSummaries, setOrderSummaries] = useState<OrderSummary[]>([]);
  const [newOrders, setNewOrders] = useState<Order[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 100;

  const fetchNewOrders = useCallback(async (lastOrderId: number) => {
    try {
      const { data, error } = await supabase
        .from('order')
        .select('*')
        .gt('id', lastOrderId)
        .order('id', { ascending: true });

      if (error) {
        throw new DataFetchError("新しい注文データの取得失敗: " + error.message);
      }

      setNewOrders(data as Order[]);
      setError(null);
    } catch (err) {
      if (err instanceof DataFetchError) {
        setError(err.message);
      } else {
        setError('予期せぬエラーが発生しました。');
      }
      console.error(err);
    }
  }, []);

  const fetchOrderSummaries = useCallback(async (page: number) => {
    const startIdx = (page - 1) * itemsPerPage;
    const endIdx = startIdx + itemsPerPage - 1;

    try {
      const { data, error, count } = await supabase
        .from('order_summary')
        .select(`
          *,
          start_order:start_order_id(*),
          last_order:last_order_id(*)
        `, { count: 'exact' })
        .order('id', { ascending: false })
        .range(startIdx, endIdx);

      if (error) {
        throw new DataFetchError("データの取得失敗: " + error.message);
      }

      setOrderSummaries(data as OrderSummary[]);
      setTotalPages(Math.ceil((count ?? 0) / itemsPerPage));
      if (data.length > 0) {
        const lastOrderId = data[0].last_order_id;
        fetchNewOrders(lastOrderId);
      }
      setError(null);
    } catch (err) {
      if (err instanceof DataFetchError) {
        setError(err.message);
      } else {
        setError('予期せぬエラーが発生しました。');
      }
      console.error(err);
    }
  }, [fetchNewOrders, itemsPerPage]);

  useEffect(() => {
    fetchOrderSummaries(page);
  }, [page, fetchOrderSummaries]);

  return { orderSummaries, newOrders, fetchOrderSummaries, error, page, totalPages, setPage };
};
